import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { AddCertificateModalComponent } from 'app/shared-pages/invite-contractor/add-certificate-modal/add-certificate-modal.component';
import { Globals } from 'app/_globals';
import { ContractorCompetencyWorkflowItem } from 'app/_models/workflow-items/contractor-competency-workflow-item';
import { CompetencyTypesService } from 'app/_services/competency-types.service';
import { ContractorManagerService } from 'app/_services/contractor-manager.service';
import { ContractorService } from 'app/_services/contractor.service';
import { FileService } from 'app/_services/file.service';
import { NotificationService } from 'app/_services/notification.service';
import { RolePermissionService, SkylarkPermission } from 'app/_services/role-permission.service';
import { WorkflowItemService } from 'app/_services/workflow-items.service';
import { environment } from 'environments/environment';
import Swal from 'sweetalert2';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { SiteOwnerUserService } from 'app/_services/site-owner-user.service';
import { TrainingService } from 'app/_services/training.service';
import { TrainingCourseForUser } from 'app/_models/training/training-course-for-user.model';
import { TrainingType } from 'app/_models/training/training-type';
import { Pagination } from 'app/_models/pagination.model';

@Component({
  selector: 'contractor-certificates',
  templateUrl: './contractor-certificates.component.html',
})
export class ContractorCertificatesComponent implements OnInit {
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;

  public missingSiteOwnerLink = false;

  private routeRole: any;
  private selectedContractorId: any;

  public tabitems: any[] = [];
  public selectedContractor: any;
  public competencyOptions = [];
  public competencies = [];
  public acceptanceOptions: any[] = [];
  public outageAcceptances: TrainingCourseForUser[] = [];
  private maintenanceSiteOwner: any;

  // add form
  public saveInProgress: boolean;
  public showAddCertificateTypeModal = false;
  public showAssignAcceptanceModal = false;
  public assignAcceptanceForm: FormGroup;
  public addCertificateForm: FormGroup;
  public selectedDocumentsArray: any[] = [];
  public areFilesAdded: boolean = false;
  public minDateValue = new Date();
  private currentYear = new Date().getFullYear();
  public datepickerYearRange = this.currentYear + ':' + (this.currentYear + 10);
  public selectedCompetencySiteOwnerId: any;
  public isReadOnly: boolean;
  public siteOwnerSkylarkUserId: any;
  public loaded: boolean = false;

  public isSiteOwnerView: boolean = false;
  public isSiteDelegateView: boolean = false;
  public isPowerStationOwnerView: boolean = false;
  public isPowerStationManagerView: boolean = false;
  public siteOwnerIds: number[] = [];
  public siteOwners: any[] = [];
  public selectedSiteOwner: any;
  public isHuntstown: boolean;
  public extraCompetencyInfo: { [competencyId: number]: string } = {};

  constructor(
    public contractorService: ContractorService,
    private notificationService: NotificationService,
    public globals: Globals,
    private competencyService: CompetencyTypesService,
    private modalService: NgbModal,
    public activatedRoute: ActivatedRoute,
    public fb: FormBuilder,
    public fileService: FileService,
    public contractorManagerService: ContractorManagerService,
    public workflowItemService: WorkflowItemService,
    private rolePermissionService: RolePermissionService,
    private siteOwnerUserService: SiteOwnerUserService,
    private trainingService: TrainingService,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedContractorId = params.contractorId;
    });

    this.routeRole = globals.user.routeRole;
    this.isSiteDelegateView = this.routeRole === 'site-delegate';
    this.isSiteOwnerView = this.routeRole === 'site-owner';
    this.isPowerStationOwnerView = this.routeRole === 'power-station-owner';
    this.isPowerStationManagerView = this.routeRole === 'power-station-manager';

    // client Huntstown specifically wants extra text displayed next to certain competencies
    if (this.isPowerStationOwnerView || this.isPowerStationManagerView) {
      this.isHuntstown = this.globals.powerStationOwner.mainSiteId === environment.HUNTSTOWN_ID;
      this.extraCompetencyInfo = environment.CONTRACTOR_COMPETENCY_ID_TO_EXTRA_INFO_TEXT;
    }

    this.tabitems = [
      { path: '/' + this.routeRole + '/contractors/', title: 'All Contractors', class: 'back-link' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/details', title: 'Details' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/certificates', title: 'Certificates' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/workers', title: 'Workers' },
    ];

    this.addCertificateForm = fb.group({
      competencyType: new FormControl(null, [Validators.required]),
      expires: new FormControl(null, [Validators.required]),
    });
    this.assignAcceptanceForm = fb.group({
      acceptance: new FormControl(null, [Validators.required]),
    });

    this.isReadOnly = !this.rolePermissionService.hasPermission(SkylarkPermission.WRITE__CONTRACTORS);
  }

  ngOnInit() {
    this.getCompetencyTypes();
    this.getSiteOwnerId();
    if (this.isPowerStationOwnerView || this.isPowerStationManagerView ) {
      //get outage acceptances
      this.getOutageAcceptanceAssignments();
      this.getAcceptanceOptions();
    }
  }

  private getCompetencyTypes() {
    if (this.isReadOnly) {
      return;
    }
    this.competencyService.getAllCompetencyTypesByType('POWERSTATION').subscribe((data: any) => {
      this.competencyOptions = data.map(competency => ({
        label: competency.value,
        value: { id: competency.id, label: competency.value },
      }));

      this.competencyOptions.sort((a, b) => a.label.localeCompare(b.label));
    });
  }

  private getSelectedContractor(id, selectedSiteOwnerId?) {
    const siteOwnerId = selectedSiteOwnerId || this.siteOwnerSkylarkUserId;

    this.contractorService.getContractorById(id).subscribe(

      (result: any) => {
      this.selectedContractor = result;

      const mso = result.maintenanceSiteOwners.find(mso => {
        return mso.siteOwnerUser.skylarkUserId === siteOwnerId;
      })

      this.maintenanceSiteOwner = mso;

      if (mso) {
        this.selectedContractor.competencies = mso.maintenanceSiteOwnerCompetencyTypes.map(c => {
          return {
            label: c.competencyType.value,
            status: c.status,
            id: c.competencyType.id,
            linkId: c.id,
            deleted: c.deleted,
            rejectionComments: c.rejectionComments,
            files: c.files,
            expiryDate: c.expiryDate,
          };
        });

        this.selectedContractor.competencies = this.selectedContractor.competencies.filter(c => !c.deleted);
        this.selectedContractor.competencies.sort((a, b) => a.label.localeCompare(b.label));
      } else {
        this.missingSiteOwnerLink = true;
      }
      this.loaded = true;
    });
  }

  private getOutageAcceptanceAssignments() {
    this.trainingService.getOutageAcceptanceListByMaintenance(this.selectedContractorId).subscribe((data: TrainingCourseForUser[]) => {
      this.outageAcceptances = data;
    });
  }

  private getAcceptanceOptions() {
    const pagination: Pagination = {
      size: 100,
      page: 0,
      totalRecords: 0,
    };
    this.trainingService.getAllTrainingCourses(pagination,['name'],'',TrainingType.Outage).subscribe((data: any) => {
      this.acceptanceOptions = data.body.map(course => ({
        label: course.trainingName,
        value: { id: course.trainingId, label: course.trainingName },
        })
      )
    });
  }

  public selectedSiteOwnerChange() {
    this.getSelectedContractor(this.selectedContractorId, this.selectedSiteOwner)
  }

  private getSiteOwnerId() {
    // figure out which siteowner this is for
    if (this.isSiteOwnerView || this.isPowerStationOwnerView || this.isPowerStationManagerView) {
      this.siteOwnerSkylarkUserId = this.globals.siteOwnerUsers[0].skylarkUserId;
      this.getSelectedContractor(this.selectedContractorId);
    } else if (this.isSiteDelegateView) {
      this.siteOwnerIds = this.globals.siteOwnerUsers.map(site => site.skylarkUserId);

      if (this.siteOwnerIds.length === 1) {
        this.siteOwnerSkylarkUserId = this.siteOwnerIds[0];
        this.getSelectedContractor(this.selectedContractorId);
      } else {
        // for users delegated by multiple site owners, need a dropdown to choose which site owner
        this.siteOwnerUserService.getAllEnabledSiteOwnersForDropdowns().subscribe(
          (data: any) => {
            data.forEach(element => {
              const obj = {
                label: element.firstName + ' ' + element.lastName,
                value: element.id
              };
              this.siteOwners.push(obj);
            });
            this.loaded = true;
          });
      }
    }
  }

  public deleteCert(id) {
    Swal.fire({
      title: 'Delete certificate/requirement?',
      text: 'You will not be able to revert this',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it',
    }).then((result) => {
      if (result.value) {
        this.contractorService.deleteContractorCertificateById(id).subscribe((result: any) => {
          this.notificationService.notify('success', `Requirement deleted`);
          if (this.siteOwnerIds.length > 1) {
            this.getSelectedContractor(this.selectedContractorId, this.selectedSiteOwner);
          } else {
            this.getSelectedContractor(this.selectedContractorId);
          }
        }, error => {
          console.error(error);
          this.notificationService.notify('danger', `Error deleting the requirement`);
          throw error;
        });
      }
    });
  }

  public openRequiredCompetencyModal() {
    const modalOptions: NgbModalOptions = { centered: true };
    const modal = this.modalService.open(AddCertificateModalComponent, modalOptions);
    modal.componentInstance.competencyOptions = this.competencyOptions;

    modal.result
      .then((result) => {
        if (result.value && Array.isArray(result.value.competency)) {
          const { remainingCompetencies, alreadyAddedMessages } = result.value.competency.reduce((acc, selectedCompetency) => {
            if (this.selectedContractor.competencies.some(c => c.id === selectedCompetency.id)) {
              acc.alreadyAddedMessages.push(`${selectedCompetency.label} already added`);
            } else {
              acc.remainingCompetencies.push(selectedCompetency);
            }
            return acc;
          }, { remainingCompetencies: [], alreadyAddedMessages: [] });

          alreadyAddedMessages.forEach(message => {
            this.notificationService.notify('warning', message);
          });

          // Submit the remaining competencies if any
          if (remainingCompetencies.length > 0) {
            this.submitNewRequiredCompetency(remainingCompetencies);
          }
        }
      })
      .catch(() => {
        /* modal dismissed */
      });
  }

  public openAssignAcceptanceModal() {
    this.assignAcceptanceForm.reset();
    this.showAssignAcceptanceModal = true;
  }

  public submitNewRequiredCompetency(competency) {
    this.saveInProgress = true
    const competencyTypeIds = Array.isArray(competency) ? competency.map(c => c.id): [competency.id];
    const body = {
      maintenanceSiteOwnerId: this.maintenanceSiteOwner.id,
      competencyIds: competencyTypeIds
    };

    this.contractorService.addNewRequirementToContractor(body).subscribe(result => {
      this.notificationService.notify('success', `Requirement added`);
      this.saveInProgress = false;

      if (this.siteOwnerIds.length > 1) {
        this.getSelectedContractor(this.selectedContractorId, this.selectedSiteOwner);
      } else {
        this.getSelectedContractor(this.selectedContractorId);
      }

    }, error => {
      console.error(error);
      this.saveInProgress = false;
      this.notificationService.notify('danger', `Error creating the requirement`);
      throw error;
    });
  }

  public viewDocument(fileName) {
    this.contractorService.showFile(environment.apiUrl + 'api/ext-file/mainteanceSiteOwnerCompetencyFiles', fileName);
  }

  //
  // upload cert for the contractor
  //

  public showAddCertificateModal() {
    this.resetFileInput();
    this.areFilesAdded = false;
    this.resetFileInput();
    this.addCertificateForm.reset();
    this.showAddCertificateTypeModal = true;
  }

  public async onAddCertificateFormSubmit() {
    this.saveInProgress = true
    const values = this.addCertificateForm.value;
    const newCompetencyTypeId = values.competencyType.value.id;

    // 1 --- create requirement
    const msocId = await this.getOrCreateRequirement(newCompetencyTypeId);

    // 2 --- upload the certificate
    const body2 = {
      expiryDate: DateTimeUtils.timelessDateAsUTCDate(values.expires).toISOString(),
    };

    this.fileService.deleteAllContractorCompetencyFiles(msocId)
    .concatMap(() => {
      return this.contractorService.createContractorCompetency(msocId, body2, this.selectedDocumentsArray);
    })
    .concatMap(() => {
      // 3 --- find the workflow item
      return this.workflowItemService.getPendingWorkflowItemsNoPagination();
    })
    .concatMap((items) => {
      // 4 --- approve the certificate
      const matchingWorkflowItem = items
          .filter(workflowItem => workflowItem.workflowType === 'CONTRACTOR_COMPETENCY')
          .find(workflowItem => (workflowItem as ContractorCompetencyWorkflowItem).maintenanceSiteOwnerCompetencyTypeId === msocId);

      if (!matchingWorkflowItem) {
        throw new Error('ContractorCompetencyWorkflowItemNotFound');
      }

       let body3 = {
        status: 'APPROVED',
      };

      return this.workflowItemService.approveOrRejectItem(matchingWorkflowItem.id, body3);
    })
    .subscribe(() => {
      this.notificationService.notify('success', 'Success', 'Certificate added and approved');
      this.saveInProgress = false;
      this.showAddCertificateTypeModal = false;
      if (this.siteOwnerIds.length > 1) {
        this.getSelectedContractor(this.selectedContractorId, this.selectedSiteOwner);
      } else {
        this.getSelectedContractor(this.selectedContractorId);
      }
    }, error => {
      console.error(error);
      if (error.message === "Infected"){
        this.notificationService.notify('danger', `Error`, `File is infected with malware!`);
      } else if (error.message === "EmbeddedScript"){
        this.notificationService.notify('danger', `Error`, `File has embedded script - please submit file to Skylark for analysis`);
      } else if (error.message === 'ContractorCompetencyWorkflowItemNotFound') {
        this.notificationService.notify('danger', `Error`, `Certificate uploaded, but error automatically approving it. Look in work flow inbox`);
      } else {
        this.notificationService.notify('danger', `Error`, `Could not upload certificate`);
      }
      this.saveInProgress = false;
      this.showAddCertificateTypeModal = false;
      if (this.siteOwnerIds.length > 1) {
        this.getSelectedContractor(this.selectedContractorId, this.selectedSiteOwner);
      } else {
        this.getSelectedContractor(this.selectedContractorId);
      }
    });
  }

  private getOrCreateRequirement(newCompetencyTypeId): Promise<any> {
    return new Promise((resolve, reject) => {
      const body = {
        maintenanceSiteOwnerId: this.maintenanceSiteOwner.id,
        competencyIds: [newCompetencyTypeId],
      };

      const existingRequirement = this.maintenanceSiteOwner.maintenanceSiteOwnerCompetencyTypes
        .filter(existingCompetency => !existingCompetency.deleted)
        .find(existingCompetency => existingCompetency.competencyType.id === newCompetencyTypeId);

      if (existingRequirement) {
        return resolve(existingRequirement.id);
      } else {
        this.contractorService.addNewRequirementToContractor(body).subscribe(
          result => {
            let msoc = (result as any).maintenanceSiteOwnerCompetencyTypes
              .filter(msoc => !msoc.deleted)
              .find(msoc => msoc.competencyType.id === newCompetencyTypeId);
            return resolve(msoc.id);
          },
          error => {
            console.error(error);
            this.saveInProgress = false;
            this.notificationService.notify('danger', `Error creating the requirement`);
            throw error;
          }
        );
      }
    });
  }

  public onFileChange(event) {
    for (let i = 0; i < event.target.files.length; i++) {
      const file = event.target.files[i];
      this.selectedDocumentsArray.push(file);
    }
    this.areFilesAdded = (this.selectedDocumentsArray.length > 0);
  }

  private resetFileInput() {
   //Reset state of file to avoid data leak
    this.selectedDocumentsArray = [];
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
  public onAssignAcceptanceFormSubmit(){
    this.saveInProgress = true;
    const values = this.assignAcceptanceForm.value;
    const acceptanceCourseId = values.acceptance.value.id;
    const body = {
      courseId: acceptanceCourseId,
      maintenanceId: this.selectedContractorId,
    };

    this.trainingService.createNewAcceptanceCourseForContractor(body).subscribe(result => {
      if (this.outageAcceptances.map(c => c.workerUserCourseId).some(id => result.id === id)) {
        this.notificationService.notify('success', 'Success', 'Course already assigned to worker');
      } else {
        this.getOutageAcceptanceAssignments();
        this.notificationService.notify('success', 'Success', 'Course assigned to worker');
      }
      this.saveInProgress = false;
      this.showAssignAcceptanceModal = false;
    }, error => {
      console.error(error);
      this.notificationService.notify('danger', 'Error', 'Could not add course to worker');
      this.saveInProgress = false;
      this.showAssignAcceptanceModal = false;
    });
  }

  public deleteAcceptanceAssignment(outageAcceptance){

    Swal.fire({
      title: 'Delete Outage Acceptance',
      text: 'You will not be able to revert this!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.saveInProgress = true;
        this.trainingService.deleteUserCourseAssigment(outageAcceptance.workerUserCourseId).subscribe((data: any) => {
          Swal.fire('Deleted!', 'Outage acceptance has been removed.', 'success');
          this.getOutageAcceptanceAssignments();
          this.saveInProgress = false;
        },
        (error: any) => {
          Swal.fire('Error', 'Could not remove outage acceptance.', 'error');
          this.saveInProgress = false;
        });
      }
    });
  }
}
